/**
 * Tiny A/B test framework — deterministic variant assignment per anonymous
 * visitor and one-shot exposure logging into the pricing-funnel analytics.
 *
 * Usage:
 *   const variant = useExperiment("nav_solutions_copy", ["solutions", "who_its_for"]);
 *   // variant is one of the provided keys, stable for this visitor.
 *   // An `experiment_exposure` event fires once per session per experiment.
 */
import { useEffect, useMemo } from "react";
import { trackEvent } from "./analytics";

const ANON_KEY = "tr_anon_id";
const EXPOSED_KEY = "tr_exp_exposed"; // session-scope set of "name:variant"

function getAnonId() {
  try {
    return localStorage.getItem(ANON_KEY) || "anon";
  } catch {
    return "anon";
  }
}

function hash(str) {
  // djb2 — fast, stable, deterministic.
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function useExperiment(name, variants) {
  const variant = useMemo(() => {
    const anon = getAnonId();
    const idx = hash(`${name}:${anon}`) % variants.length;
    return variants[idx];
  }, [name, variants]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(EXPOSED_KEY);
      const seen = raw ? new Set(JSON.parse(raw)) : new Set();
      const key = `${name}:${variant}`;
      if (!seen.has(key)) {
        seen.add(key);
        sessionStorage.setItem(EXPOSED_KEY, JSON.stringify(Array.from(seen)));
        trackEvent("experiment_exposure", { experiment: name, variant });
      }
    } catch {
      /* no-op */
    }
  }, [name, variant]);

  return variant;
}

/**
 * trackExperimentClick — call from onClick handlers that participate in an
 * experiment so we can measure conversion.
 */
export function trackExperimentClick(name, variant, extra) {
  trackEvent("experiment_click", { experiment: name, variant, ...(extra || {}) });
}
